import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './SportSignageView.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PDF_URL = '/img/signage_schedule_2026.pdf';
const MIN_SCALE = 0.5;
const MAX_SCALE = 2.0;
const SCALE_STEP = 0.25;

const SportSignageView: React.FC = () => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(false);
  }, []);

  const onDocumentLoadError = useCallback(() => {
    setLoading(false);
    setError(true);
  }, []);

  const prevPage = () => setCurrentPage(p => Math.max(1, p - 1));
  const nextPage = () => setCurrentPage(p => Math.min(numPages, p + 1));
  const zoomOut = () => setScale(s => Math.max(MIN_SCALE, parseFloat((s - SCALE_STEP).toFixed(2))));
  const zoomIn = () => setScale(s => Math.min(MAX_SCALE, parseFloat((s + SCALE_STEP).toFixed(2))));
  const resetZoom = () => setScale(1.0);

  return (
    <div className="signage-view">
      <div className="signage-header">
        <div className="signage-title-section">
          <h2 className="signage-title">2026 Sports Signage Schedule</h2>
          <p className="signage-subtitle">
            Your brand placement schedule across live events for 2026
          </p>
        </div>
        <a
          href={PDF_URL}
          download="signage_schedule_2026.pdf"
          className="signage-download-btn"
        >
          <i className="material-icons">download</i>
          Download PDF
        </a>
      </div>

      <div className="signage-content">
        {loading && !error && (
          <div className="signage-loading">
            <div className="signage-spinner"></div>
            <p>Loading schedule...</p>
          </div>
        )}

        {error && (
          <div className="signage-error">
            <i className="material-icons">error_outline</i>
            <p>Unable to load the schedule document.</p>
            <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="signage-download-btn">
              <i className="material-icons">open_in_new</i>
              Open PDF directly
            </a>
          </div>
        )}

        <div className={`signage-viewer ${loading ? 'signage-viewer--hidden' : ''}`}>
          <Document
            file={PDF_URL}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
          >
            <div className="signage-page-wrapper">
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={null}
              />
            </div>
          </Document>
        </div>
      </div>

      {!loading && !error && numPages > 0 && (
        <div className="signage-nav">
          <div className="signage-nav-pages">
            <button
              className="signage-nav-btn"
              onClick={prevPage}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <i className="material-icons">chevron_left</i>
            </button>
            <span className="signage-page-indicator">
              Page <strong>{currentPage}</strong> of <strong>{numPages}</strong>
            </span>
            <button
              className="signage-nav-btn"
              onClick={nextPage}
              disabled={currentPage >= numPages}
              aria-label="Next page"
            >
              <i className="material-icons">chevron_right</i>
            </button>
          </div>

          <div className="signage-nav-divider" />

          <div className="signage-nav-zoom">
            <button
              className="signage-nav-btn"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              aria-label="Zoom out"
            >
              <i className="material-icons">remove</i>
            </button>
            <button
              className="signage-zoom-label"
              onClick={resetZoom}
              title="Reset zoom"
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              className="signage-nav-btn"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              aria-label="Zoom in"
            >
              <i className="material-icons">add</i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SportSignageView;
